import { convertExcelDateToComputerDate } from '../excel';

/**
 * Maps Order Report-Detail CSV format to Warehouse Target format
 * @param {Array} csvData - Array of CSV rows
 * @returns {Array} Mapped data in target format
 */
export const mapOrderReportToTargetFormat = (csvData) => {
  if (!csvData || csvData.length === 0) {
    return [];
  }

  // Find the header row (usually row 7 in the CSV format)
  let headerRowIndex = -1;
  let headerRow = null;

  for (let i = 0; i < csvData.length; i++) {
    if (
      csvData[i] &&
      csvData[i].length > 0 &&
      csvData[i][0] &&
      (csvData[i][0] === 'Region' ||
        (csvData[i][0] === 'SNo' && csvData[i][2] === 'Voucher No'))
    ) {
      headerRowIndex = i;
      headerRow = csvData[i];
      break;
    }
  }

  if (headerRowIndex === -1 || !headerRow) {
    console.error(
      'Could not find header row. Available rows:',
      csvData.slice(0, 10).map((row, i) => ({ index: i, firstCell: row[0] }))
    );
    throw new Error('Could not find header row in Order Report-Detail format');
  }

  console.log('Found header row at index:', headerRowIndex);

  // Get data rows (skip header and any metadata rows)
  const dataRows = csvData.slice(headerRowIndex + 1);

  // Create column mapping
  const columnMap = {};
  headerRow.forEach((column, index) => {
    columnMap[column] = index;
  });

  console.log('Number of data rows:', dataRows.length);

  // Define product names to exclude
  const excludedProductStrings = [
    'HANDLING CHARGES',
    'EWS',
    'SPL-SET',
    'VS+ EW',
    'SPL - SET',
    'DELIVERY CHARGES',
  ];

  console.log('Excluding products containing:', excludedProductStrings);

  // Map each data row to target format
  const mappedData = dataRows
    .map((row, index) => {
      if (!row || row.length === 0) return null;

      // Helper function to get value safely
      const getValue = (columnName) => {
        const colIndex = columnMap[columnName];
        const value = colIndex !== undefined ? row[colIndex] || '' : '';
        return value;
      };

      // Helper function to parse number
      const parseNumber = (value) => {
        if (!value) return 0;
        const num = parseFloat(value.toString().replace(/,/g, ''));
        return isNaN(num) ? 0 : num;
      };

      const returnDate = (datePassed) => {
        let newDate = null;
        if (typeof datePassed === 'number') {
          newDate = convertExcelDateToComputerDate(datePassed, true);
        } else {
          const normalized = datePassed.replace(/\//g, '-');
          const [date, month, year] = normalized.split('-');
          newDate = new Date(`${year}-${month}-${date}`);
        }
        return String(newDate).slice(4, 15);
      };
      // Get product name and check if it should be excluded
      const productName =
        getValue('ProductName') ||
        getValue('ProductFullName') ||
        getValue('Product') ||
        '';
      const shouldExclude = excludedProductStrings.some((excludedString) =>
        productName.toUpperCase().includes(excludedString.toUpperCase())
      );

      // Skip this row if product should be excluded
      if (shouldExclude) {
        console.log(`Excluding product: ${productName}`);
        return null;
      }

      return {
        SNo: index + 1,
        'Voucher No': getValue('Voucher No'),
        Customer: getValue('Customer') || '',
        'Pending Quantity': 1,
        Date: returnDate(getValue('Date')),
        'Delivery Date': returnDate(
          getValue('DeliveryDate') || getValue('Delivery Date')
        ),
        Product:
          getValue('ProductName') ||
          getValue('ProductFullName') ||
          getValue('Product') ||
          '',
        'Product Category':
          getValue('Department') || getValue('Product Category'),
        'Product Code': getValue('Product Code'),
        Brand: getValue('Brand') || '',
        'VA Nnumber':
          parseNumber(getValue('AlternateCode') || getValue('VAN Number')) || 0,
        'Item Van No':
          parseNumber(getValue('AlternateCode') || getValue('VAN Number')) || 0,
        Remarks: getValue('Remarks'),
        'Priority Delivery': getValue('Priority Delivery') || '',
        'Customer Code': getValue('Customer Code'),
      };
    })
    .filter(Boolean); // Remove null entries

  console.log('Total mapped records:', mappedData.length);
  console.log('Total excluded products:', dataRows.length - mappedData.length);
  if (mappedData.length > 0) {
    console.log('Sample mapped record keys:', Object.keys(mappedData[0]));
  }
  return mappedData;
};

/**
 * Detects if the uploaded data is in Order Report-Detail format
 * @param {Array} fileData - File data from upload
 * @returns {boolean} True if it's Order Report-Detail format
 */
export const isOrderReportFormat = (fileData) => {
  if (!fileData || !fileData.rows || fileData.rows.length === 0) {
    return false;
  }

  // Check if it contains the characteristic header of Order Report-Detail
  const firstFewRows = fileData.rows.slice(0, 10);

  for (let i = 0; i < firstFewRows.length; i++) {
    const row = firstFewRows[i];
    if (row && row.length > 0) {
      const rowText = row.join(' ').toLowerCase();
      if (
        rowText.includes('orders report - detail') ||
        rowText.includes('region,store,alternatestorecode') ||
        (row[0] && row[0] === 'Region') ||
        rowText.includes('all target pending report')
      ) {
        console.log('Found Order Report format at row:', i);
        return true;
      }
    }
  }
  return false;
};

/**
 * Detects if the uploaded data is in Warehouse Target format
 * @param {Array} fileData - File data from upload
 * @returns {boolean} True if it's Warehouse Target format
 */
export const isWarehouseTargetFormat = (fileData) => {
  if (!fileData || !fileData.rows || fileData.rows.length === 0) {
    return false;
  }

  // Check if it contains the characteristic header of Warehouse Target format
  const firstFewRows = fileData.rows.slice(0, 5);

  for (let row of firstFewRows) {
    if (row && row.length > 0) {
      const rowText = row.join(' ').toLowerCase();
      if (
        rowText.includes('sno') &&
        (rowText.includes('voucher no') || rowText.includes('voucher'))
      ) {
        return true;
      }
    }
  }

  return false;
};
